// src/Dashboard/admin/sections/HierarchySection.jsx
import { RefreshCw, Building2 } from "lucide-react";

export default function HierarchySection({
  // data
  conferences = [],
  stations = [],
  districts = [],
  churches = [],
  availableChurches = [],

  // helpers (from AdminDashboard)
  getIsActive,
  getConferenceId,
  getStationId,
  getDistrictId,
  getChurchId,

  // loading + actions
  loadingHierarchy,
  onReloadHierarchy,
}) {
  const activeConfs = (conferences || []).filter((c) => getIsActive?.(c) !== "0");
  const activeChurches = ((availableChurches?.length ? availableChurches : churches) || [])
    .filter((c) => getIsActive?.(c) !== "0")
    .slice(0, 120);

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Hierarchy Browser</h2>
            <p className="text-xs text-slate-500">
              View hierarchy data pulled from your SDA hierarchy API.
            </p>
          </div>

          <button
            onClick={onReloadHierarchy}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <RefreshCw size={16} className={loadingHierarchy ? "animate-spin" : ""} />
            Reload
          </button>
        </div>

        <div className="mt-5 grid md:grid-cols-4 gap-4">
          <MiniStatusCard title="Conferences" value={conferences.length} subtitle="records" icon={Building2} />
          <MiniStatusCard title="Stations" value={stations.length} subtitle="records" icon={Building2} />
          <MiniStatusCard title="Districts" value={districts.length} subtitle="records" icon={Building2} />
          <MiniStatusCard title="Churches" value={churches.length} subtitle="records" icon={Building2} />
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800/70 px-4 py-3 text-sm font-semibold">
              Conferences
            </div>
            <div className="max-h-[360px] overflow-auto">
              {activeConfs.map((c) => (
                <div
                  key={String(getConferenceId?.(c))}
                  className="px-4 py-3 border-t border-slate-100 dark:border-slate-800"
                >
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    {String(getConferenceId?.(c))}
                  </div>
                </div>
              ))}

              {activeConfs.length === 0 && (
                <div className="px-4 py-8 text-sm text-slate-500">
                  No conferences available.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800/70 px-4 py-3 text-sm font-semibold">
              Churches (filtered)
            </div>
            <div className="max-h-[360px] overflow-auto">
              {activeChurches.map((c) => (
                <div
                  key={String(getChurchId?.(c))}
                  className="px-4 py-3 border-t border-slate-100 dark:border-slate-800"
                >
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    {String(getChurchId?.(c))}
                  </div>
                </div>
              ))}

              {activeChurches.length === 0 && (
                <div className="px-4 py-8 text-sm text-slate-500">
                  No churches in this scope.
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500">
              Tip: use the Scope filters above to narrow down churches.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Local mini UI card (kept inside section for now)
   Later we can move to /admin/components if you want.                         */
/* -------------------------------------------------------------------------- */

function MiniStatusCard({ title, value, subtitle, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60">
          <Icon size={18} className="text-slate-700 dark:text-slate-200" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500">{title}</div>
          <div className="text-base font-semibold truncate">{String(value)}</div>
          <div className="text-xs text-slate-400 truncate">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}
